import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import type { TumTenant } from '@/types/tum';

export const useTumTenants = () => {
  const workspace = useWorkspace();
  const currentWorkspace = workspace?.currentWorkspace;
  const queryClient = useQueryClient();

  const { data: tenants, isLoading, error } = useQuery({
    queryKey: ['tum-tenants', currentWorkspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tum_tenants')
        .select('*')
        .eq('workspace_id', currentWorkspace!.id)
        .order('display_name', { ascending: true });
      
      if (error) throw error;
      return data as TumTenant[];
    },
    enabled: !!currentWorkspace?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (tenant: Omit<TumTenant, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by' | 'workspace_id'>) => {
      const { data, error } = await supabase
        .from('tum_tenants')
        .insert({ ...tenant, workspace_id: currentWorkspace!.id })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-tenants'] });
      toast.success('Tenant created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create tenant: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TumTenant> & { id: string }) => {
      const { data, error } = await supabase
        .from('tum_tenants')
        .update(updates)
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-tenants'] });
      toast.success('Tenant updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update tenant: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tum_tenants')
        .delete()
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-tenants'] });
      toast.success('Tenant deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete tenant: ${error.message}`);
    },
  });

  return {
    tenants: tenants || [],
    isLoading,
    error,
    createTenant: createMutation.mutate,
    updateTenant: updateMutation.mutate,
    deleteTenant: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
